import { Component, OnInit, Input, SimpleChange } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChangeClientDialogComponent } from '../../../shared/components/change-client-dialog/change-client-dialog.component';
import { DialogNotificationsComponent } from '../../../shared/components/dialog-notifications/dialog-notifications.component';
import { ImpersonateDialogComponent } from '../../../shared/components/impersonate-dialog/impersonate-dialog.component';
import { ClientInterface } from '../../../shared/interfaces/client-data';
import { ClientsService } from '../../../shared/services/clients.service';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { EmployeeService } from '../../../shared/services/employee.service';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';

@Component({
    selector: 'top-nav-bar',
    templateUrl: './top-nav-bar.component.html',
    styleUrls: ['./top-nav-bar.component.scss'],
})
export class TopNavBarComponent implements OnInit {
    @Input() sideNavWidth: number;
    drawerOverflow = 'hidden';
    mobileMenuOpened = false;
    menuButtonVisible = 'visible';
    isSuperUser = false;
    isPlatformUser = false;
    isDemoUser = false;
    isLBA = false;
    clientFullName: string;
    clientShortName: string;
    keepMobileOpen = false;
    canImpersonate: boolean;
    profilePicSrc: string;
    allClients: ClientInterface[] = [];
    userFullName: string;
    isClientPowerUser: boolean;
    unreadNotificationCount: number;

    changeClientVisible: boolean = false;
    changeUserVisible: boolean = false;

    /* Profile Picture properties */
    userEmail: string;
    primaryIdentifier: string;
    pictureChange: boolean = false;
    showFiller = false;
    navWidth = 56;

    constructor(
        public dialog: MatDialog,
        private impersonationService: ImpersonationService,
        private notificationService: NotificationService,
        private clientService: ClientsService,
        private currentUserStateService: CurrentUserStateService,
        private employeeService: EmployeeService,
        private userAccessService: UserAccessService
    ) {
        this.currentUserStateService.accessAssigned.subscribe((res) => {
            if (res) {
                if (this.currentUserStateService.isLoggedIn()) {
                    this.userEmail = this.currentUserStateService.currentUser.username;
                    this.showMenuByUserType();
                    this.updateLbaImpersonationMessage();
                    this.getEmployeeIdentifier();
                    this.userFullName = this.getFirstAndLastName();
                }
            }
        });

        this.impersonationService.impersonatingClientSubject.subscribe({
            next: (res) => {
                if (res) {
                    this.updateLbaImpersonationMessage();
                }
            },
        });

        this.notificationService
            .getUnreadNotificationCount()
            .subscribe((count) => (this.unreadNotificationCount = count));
    }

    ngOnInit() {}

    ngOnChanges(changes: { [property: string]: SimpleChange }) {
        let change: SimpleChange = changes['sideNavWidth'];
        if (change && change.currentValue) {
            this.navWidth = change.currentValue;
        }
    }

    toggleMobileMenu() {
        if (!this.keepMobileOpen) {
            this.mobileMenuOpened = !this.mobileMenuOpened;
            this.menuButtonVisible == 'visible' ? 'hidden' : 'visible';
            this.drawerOverflow = 'hidden';
        }
    }

    closeMobileMenu() {
        if (this.mobileMenuOpened) {
            this.mobileMenuOpened = false;
            this.menuButtonVisible = 'visible';
            this.drawerOverflow = 'hidden';
        }
    }

    get showNav() {
        return this.currentUserStateService.isLoggedIn();
    }

    openNotificationsDialog() {
        const notificationDialogRef = this.dialog.open(DialogNotificationsComponent, {
            panelClass: 'notifications-dialog-container',
        });
    }

    openChangeClientDialog() {
        if (
            this.currentUserStateService.isLBASuperUser() ||
            this.currentUserStateService.isPlatformUser()
        ) {
            this.changeClientVisible = true;
        }
    }

    closeChangeClientDialog() {
        this.changeClientVisible = false;
    }

    closeChangeUserDialog() {
        this.changeUserVisible = false;
    }

    openChangeUserDialog() {
        this.changeUserVisible = true;
    }

    updateLbaImpersonationMessage() {
        if (this.currentUserStateService.currentUser.clientShortName === 'LBA') {
            this.isLBA = true;
            if (
                !this.impersonationService.isImpersonatingClient() ||
                !this.impersonationService?.impersonateClient
            ) {
                this.impersonationService.startImpersonatingClient('Big Home');
            }

            this.clientShortName = this.impersonationService.impersonateClient;

            this.clientService
                .getClientData(this.impersonationService.impersonateClient)
                .subscribe((data) => {
                    this.clientFullName = data.clientFullname;
                });
        } else {
            this.clientShortName = this.currentUserStateService.currentUser.clientShortName;
            this.clientFullName = 'LBA Ware';
        }
    }

    showMenuByUserType() {
        if (this.currentUserStateService.isLBASuperUser()) {
            this.isSuperUser = true;
        } else if (this.currentUserStateService.isPowerUser()) {
            if (this.currentUserStateService.isDemoUser()) {
                this.isDemoUser = true;
            } else {
                this.isClientPowerUser = true;
            }
        } else if (this.currentUserStateService.isPlatformUser()) {
            this.isPlatformUser = true;
        } else {
            this.isClientPowerUser = this.isSuperUser = this.isPlatformUser = false;
        }
    }

    isUserLoggedIn() {
        return this.currentUserStateService.isLoggedIn();
    }

    getEmployeeIdentifier() {
        if (this.currentUserStateService.isLBASuperUser()) {
            this.primaryIdentifier = null;
        } else {
            let email: string = this.userEmail;
            this.employeeService.getEmployeeIdentifierFromEmail(email).subscribe(
                (primaryIdentifier) => {
                    this.primaryIdentifier = primaryIdentifier;
                    this.pictureChange = !this.pictureChange; // Notify picture component of updated primary identifier
                },
                (error) => {
                    throw new Error(error);
                }
            );
        }
    }

    getFirstAndLastName() {
        if (!this.currentUserStateService.isLoggedIn()) {
            return;
        }

        return this.currentUserStateService.currentUser.firstAndLastName;
    }

    logOut() {
        this.clientFullName = this.clientShortName = '';
        if (this.currentUserStateService.isLoggedIn()) {
            this.currentUserStateService.logout();
        }
    }

    isImpersonatingUser() {
        return this.impersonationService.isImpersonatingUser();
    }

    canImpersonateUser(): boolean {
        return (
            (this.isSuperUser &&
                !this.isImpersonatingUser() &&
                this.impersonationService.isImpersonatingClient()) ||
            (this.isClientPowerUser && !this.isImpersonatingUser()) ||
            (this.isPlatformUser &&
                !this.isImpersonatingUser() &&
                this.impersonationService.isImpersonatingClient())
        );
    }

    searchRows() {}

    changeDrawerOverflow(val: boolean) {
        this.drawerOverflow = val ? 'hidden' : 'visible';
    }

    changeMobileOpen(val: boolean) {
        this.keepMobileOpen = val;
        this.toggleMobileMenu();
    }

    navigateToHome() {
        this.userAccessService.navigateToHomePage();
    }
}
